"use client";

import { ChevronDownIcon, Loader2 } from "lucide-react";
import type { ReactNode } from "react";

import { Shimmer } from "@/components/ai-elements/shimmer";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

type MessageActivityGroupProps = {
  /** Collapsed summary label, e.g. "Web search". */
  label: string;
  /** Number of steps folded into this group, duplicates included. */
  stepCount: number;
  /** True while the run is still being produced. */
  isStreaming: boolean;
  /** Label of the step currently in flight; shown instead of `label` while streaming. */
  currentLabel: string;
  children: ReactNode;
};

/**
 * Collapses a consecutive run of agent activity — thinking blocks and tool
 * calls — into one expandable summary row (issue #1458).
 *
 * The group stays collapsed throughout, streaming included: while the run is in
 * flight the trigger names the step currently executing so the user can see the
 * agent working, but the steps themselves only appear when the user asks for
 * them. Expanding is always a deliberate act, never something the transcript
 * does on its own.
 */
export function MessageActivityGroup({
  label,
  stepCount,
  isStreaming,
  currentLabel,
  children,
}: MessageActivityGroupProps) {
  return (
    <Collapsible className="not-prose my-2" defaultOpen={false}>
      <CollapsibleTrigger
        data-testid="message-activity-group-toggle"
        className={cn(
          "group/activity flex w-full items-center gap-1.5 text-sm text-muted-foreground",
          "transition-colors hover:text-foreground",
        )}
      >
        {isStreaming && <Loader2 className="size-3.5 shrink-0 animate-spin" />}
        <span className="min-w-0 truncate text-start">
          {isStreaming ? <Shimmer duration={1}>{currentLabel}</Shimmer> : label}
        </span>
        <span className="shrink-0 text-muted-foreground/60">·</span>
        <span className="shrink-0">
          {stepCount} {stepCount === 1 ? "step" : "steps"}
        </span>
        <ChevronDownIcon
          className={cn(
            "size-3.5 shrink-0 transition-transform",
            "group-data-[state=open]/activity:rotate-180",
          )}
        />
      </CollapsibleTrigger>

      <CollapsibleContent
        data-testid="message-activity-group"
        className={cn(
          "data-[state=closed]:animate-out data-[state=open]:animate-in",
          "data-[state=closed]:fade-out-0 data-[state=open]:slide-in-from-top-2",
        )}
      >
        <div className="mt-1 flex flex-col gap-1 border-s-2 border-border ps-3">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * Wraps one step inside a group. Identical consecutive steps are merged by
 * `buildActivitySegments`, so a merged step carries a repeat count.
 */
export function MessageActivityStep({
  duplicateCount,
  children,
}: {
  duplicateCount: number;
  children: ReactNode;
}) {
  if (duplicateCount <= 1) return <>{children}</>;

  return (
    <div className="flex min-w-0 items-center gap-2">
      <div className="min-w-0 flex-1">{children}</div>
      <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
        ×{duplicateCount}
      </span>
    </div>
  );
}
